// oxlint-disable-next-line no-restricted-imports -- the one place that wraps it
import { createI18n, Translation, useI18n as useVueI18n } from 'vue-i18n';

import enUS from '@/i18n/messages/en-US';
import { pseudoLocale, pseudoWrap } from '@/i18n/pseudo';

type LeafKeys<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : LeafKeys<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

export type MessageKey = LeafKeys<typeof enUS>;

/** vue-i18n's own `t` takes any string; this one only accepts keys present in `en-US.ts`. */
export interface Translate {
  (key: MessageKey): string;
  (key: MessageKey, plural: number): string;
  (key: MessageKey, named: Record<string, unknown>): string;
  (key: MessageKey, named: Record<string, unknown>, plural: number): string;
}

export const i18n = createI18n({
  legacy: false,
  locale: 'en-US',
  messages: { 'en-US': enUS },
  // Tests fail on console warnings (tests/unit/fail-vue-warn.js), so a missing dynamic key fails too.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
  postTranslation: pseudoLocale
    ? (message) => (typeof message === 'string' ? pseudoWrap(message) : message)
    : undefined,
});

/** `<i18n-t>` for a sentence with an embedded link. Pass `scope="global"`; `keypath` is not key-checked. */
export const I18nT = Translation;

/** Global-scope `useI18n` with a key-checked `t`. Import this, never `vue-i18n` directly. */
export function useI18n(): { t: Translate } {
  const { t } = useVueI18n({ useScope: 'global' });
  return { t: t as Translate };
}
